/**
 * Hook to initialize chat view data and connections.
 */

import { useUser } from '@clerk/clerk-react'
import { selectIsBalanceDepleted } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { useChatVirtualList } from '@/components/chat/use-chat-virtual-list.hook'
import { useUserInteractionCards } from '@/hooks/billing/use-user-interaction-cards.hook'
import { useAgentsWebsocketConfig } from '@/hooks/use-agents-websocket-config.hook'
import { useOrgMembersLookup } from '@/hooks/use-org-members-lookup.hook'
import { useChatConversation } from './use-chat-conversation.hook'
import { useChatInput } from './use-chat-input.hook'
import { useChatMessages } from './use-chat-messages.hook'
import { useChatParticipantSetup } from './use-chat-participant-setup.hook'
import { useChatPinningSetup } from './use-chat-pinning-setup.hook'
import { useChatSideEffects } from './use-chat-side-effects.hook'

const useChatInitBlocks = () => {
  const { user } = useUser()
  const { userLookup } = useOrgMembersLookup()
  const websocket = useAgentsWebsocketConfig()
  const conversationState = useChatConversation()
  const isBalanceDepleted = useAppSelector(selectIsBalanceDepleted)
  const userInteractionCardList = useUserInteractionCards()
  const input = useChatInput({ agencySlug: conversationState.agencySlug ?? undefined })
  const { participantList, participantActions } = useChatParticipantSetup({
    conversationId: conversationState.conversationId,
    conversation: conversationState.conversation,
    userLookup,
  })
  const messages = useChatMessages({
    conversationId: conversationState.conversationId,
    agencySlug: conversationState.agencySlug,
    userLookup,
    onNewConversation: conversationState.navigateToConversation,
    websocket,
    userInteractionCardList,
  })
  return {
    user,
    userLookup,
    websocket,
    conversationState,
    isBalanceDepleted,
    userInteractionCardList,
    input,
    participantList,
    participantActions,
    messages,
  }
}

export const useChatViewInit = () => {
  const base = useChatInitBlocks()
  const virtualListBinding = useChatVirtualList({
    itemList: base.messages.itemList,
    conversationId: base.conversationState.conversationId ?? undefined,
    isFollowOutputEnabled: true,
    isFetchingMoreMessages: base.messages.isFetchingMoreMessages,
    hasMoreMessages: base.messages.hasMoreMessages,
    onLoadMore: base.messages.loadMoreMessages,
  })
  const pinning = useChatPinningSetup({
    conversationId: base.conversationState.conversationId,
    virtualListBinding,
    itemList: base.messages.itemList,
    userLookup: base.userLookup,
    onInvite: base.participantActions.openAddParticipantDialog,
    onLeave: base.participantActions.openLeaveDialog,
  })
  const sideEffects = useChatSideEffects({
    conversationId: base.conversationState.conversationId,
    agencySlug: base.conversationState.agencySlug,
    input: base.input,
    messages: base.messages,
    websocket: base.websocket,
  })
  return assembleChatViewInitReturn({ base, virtualListBinding, pinning, sideEffects })
}

interface AssembleParams {
  base: ReturnType<typeof useChatInitBlocks>
  virtualListBinding: ReturnType<typeof useChatVirtualList>
  pinning: ReturnType<typeof useChatPinningSetup>
  sideEffects: ReturnType<typeof useChatSideEffects>
}

const assembleChatViewInitReturn = ({
  base,
  virtualListBinding,
  pinning,
  sideEffects,
}: AssembleParams) => ({
  user: base.user,
  websocket: base.websocket,
  conversationState: base.conversationState,
  input: base.input,
  splitViewActions: sideEffects.splitViewActions,
  participantList: base.participantList,
  participantActions: base.participantActions,
  headerActions: pinning.headerActions,
  messages: base.messages,
  ...sideEffects.sendHandler,
  ...sideEffects.stopExecution,
  ...sideEffects.attachmentDownload,
  isBalanceDepleted: base.isBalanceDepleted,
  isPlanRestricted: sideEffects.isPlanRestricted,
  userInteractionCardList: base.userInteractionCardList,
  virtualListBinding,
  pinnedMessageList: pinning.pinnedMessageList,
  highlightedMessageId: pinning.scroll.highlightedMessageId,
  jumpingPinnedMessageId: pinning.scroll.jumpingTargetId,
})
